import React, { useId } from 'react';
import { X } from 'lucide-react';
import { cx } from './classNames';
import { Portal } from './Portal';
import { useOverlayBehavior } from './overlayBehavior';
import './ui-overlay.css';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** 見出しの下に置く 1 行の補足 */
  description?: React.ReactNode;
  size?: ModalSize;
  /** 右下のボタン群。左端に寄せたい要素は footerStart へ */
  footer?: React.ReactNode;
  /** フッタ左端（「削除」など、主導線と離したいもの） */
  footerStart?: React.ReactNode;
  /**
   * 背景クリックで閉じる。入力途中の誤爆を避けたいフォームでは false にする。
   * Escape も同時に無効化したい場合は closeOnEscape を使う。
   */
  dismissOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  /** 閉じるボタンの読み上げ名 */
  closeLabel?: string;
  className?: string;
  children?: React.ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  footerStart,
  dismissOnBackdrop = true,
  closeOnEscape = true,
  closeLabel = '閉じる',
  className,
  children,
}: ModalProps) {
  const containerRef = useOverlayBehavior({ open, onClose, closeOnEscape });
  const autoId = useId();
  const titleId = `ui-modal-title-${autoId}`;
  const descId = `ui-modal-desc-${autoId}`;

  if (!open) return null;

  return (
    <Portal>
      <div
        className="ui-overlay ui-overlay--center"
        data-testid="ui-modal-overlay"
        onMouseDown={(event) => {
          // 中で押して外で離した場合に閉じないよう、mousedown の対象で判定する
          if (dismissOnBackdrop && event.target === event.currentTarget) onClose();
        }}
      >
        <div
          ref={containerRef}
          className={cx('ui-modal', `ui-modal--${size}`, className)}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description != null ? descId : undefined}
          tabIndex={-1}
          data-testid="ui-modal"
        >
          <header className="ui-modal__header">
            <div className="ui-modal__heading">
              <h2 className="ui-modal__title" id={titleId}>
                {title}
              </h2>
              {description != null && (
                <p className="ui-modal__desc" id={descId}>
                  {description}
                </p>
              )}
            </div>
            <button type="button" className="ui-overlay__close" onClick={onClose} aria-label={closeLabel}>
              <X aria-hidden="true" />
            </button>
          </header>

          <div className="ui-modal__body">{children}</div>

          {(footer != null || footerStart != null) && (
            <footer className="ui-modal__footer">
              <div className="ui-modal__footer-start">{footerStart}</div>
              <div className="ui-modal__footer-end">{footer}</div>
            </footer>
          )}
        </div>
      </div>
    </Portal>
  );
}
